import type { PublicArea } from '@shared/types/restaurant';

/**
 * Render-time localization for area names/descriptions.
 *
 * Contract (meitre-backend docs/BOOKING_FLOW_FE_INTEGRATION.md §2, "Note on
 * translations"): each restaurant-level `publicAreas` entry carries a
 * `translations` envelope keyed by base locale (en/es/pt). Top-level
 * `name`/`description` are canonical and never change with the viewer's
 * locale; a locale with no stored translation reads `null` (never an
 * empty-string copy), so `??` is the correct fallback operator.
 *
 * Expect the fallback to be the common path early in the es/pt rollout — that
 * is a rollout expectation, not part of the contract above, and it stops being
 * true as coverage fills in.
 *
 * IMPORTANT: these helpers are for display only, called from JSX. Never feed
 * their output into matching or state. `menuMatchesArea` (utils/areaUtils.ts)
 * matches primarily by area id via `areasWhereItIsOffered`; only its legacy
 * scalar fallback compares `menu.area` against the canonical `area.name` — but
 * that branch is live, so a localized name must never reach it.
 * `booking.selectedArea` stores the canonical name for the same reason; today's
 * commit payload sends only the numeric id (services/commit/shared.ts).
 */

export interface AreaDisplayStrings {
  name: string;
  description: string;
}

/**
 * 'es-AR' → 'es'. Same convention as the i18n config (`load: 'languageOnly'`,
 * `convertDetectedLanguage: (lng) => lng.split('-')[0]`) — normally
 * `i18n.language` is already a base tag, this is defensive.
 */
function baseLocale(locale: string): string {
  return (locale || '').split('-')[0].toLowerCase();
}

/**
 * Pick the localized display strings for a restaurant-level `publicAreas`
 * entry, falling back per field to the canonical values.
 *
 * Only call this on a `publicAreas` entry. A slot-level `availableAreas` entry
 * type-checks here (`AvailableArea = PublicArea & { available }`) but never
 * carries `translations`, so it would silently no-op — resolve those through
 * `localizeAreaById` instead.
 *
 * Note: `??` (not `||`) — an actual empty-string translation is a real stored
 * value and renders as-is. This deliberately diverges from the menus
 * precedent (SelectExperiencePage enrichMenus uses per-field `||`) because
 * the areas contract explicitly specifies null-means-missing semantics.
 */
export function localizePublicArea(
  area: Pick<PublicArea, 'name' | 'description' | 'translations'>,
  locale: string,
): AreaDisplayStrings {
  // Own-property lookup: plain bracket access resolves through the prototype
  // chain, and `translations['constructor'].name` is the non-nullish string
  // 'Object', which would defeat the `??` fallback and render as the area name.
  const key = baseLocale(locale);
  const translation =
    area.translations && Object.prototype.hasOwnProperty.call(area.translations, key)
      ? area.translations[key]
      : undefined;
  return {
    name: translation?.name ?? area.name,
    description: translation?.description ?? area.description,
  };
}

/**
 * Resolve a slot-level area (or any `{id, name}` reference such as
 * `booking.selectedArea`) to localized display strings. Slot `availableAreas`
 * entries do NOT carry translations — only restaurant-level `publicAreas`
 * does — so we map by id into `publicAreas` and localize from there, falling
 * back to the reference's own canonical values when there is no match.
 *
 * Ids are compared as strings because `booking.selectedArea.id` is stored as
 * a string while `publicAreas[].id` is a number.
 */
export function localizeAreaById(
  areaRef: { id: string | number; name: string; description?: string | null },
  publicAreas: readonly PublicArea[] | undefined,
  locale: string,
): AreaDisplayStrings {
  // Not just `?.` — the restaurant payload is cast from `response.data` with no
  // runtime shape check, and PHP's json_encode emits `{}` (not `[]`) for a
  // non-sequentially-keyed array. A bare `.find()` on that would throw during
  // render, and these surfaces sit on the booking path outside any ErrorBoundary.
  const match = Array.isArray(publicAreas)
    ? publicAreas.find((a) => String(a.id) === String(areaRef.id))
    : undefined;
  if (match) return localizePublicArea(match, locale);
  return { name: areaRef.name, description: areaRef.description ?? '' };
}
